/**
 * The channel editor: where an owner writes their guidelines.
 *
 * `docs/plans/2026-09-03-channel-surfaces-and-the-creation-door.md` §4.
 *
 * **THE FLOOR RENDERS BESIDE THE OWNER'S CLAUSES, READ-ONLY, AND THAT IS THE
 * MOST IMPORTANT THING ON THE PAGE.** An owner who cannot see what they may not
 * change will write a clause that gets refused and read the refusal as a bug.
 * The whole product argument is that the bar is checkable, and the first person
 * who needs to check it is the person writing underneath it.
 *
 * It imports `CLAUSES` rather than fetching a description of them, so what is
 * shown is the literal text sent to a reviewer. A second copy served over an
 * endpoint would be a copy that drifts, which this repository has been bitten by
 * three times.
 *
 * **NO DRAFT STATE.** A channel has published rules or it does not exist. A
 * draft is a fourth thing that can disagree with the two the schema holds, and
 * `channel_versions` is append-only precisely so there is one answer.
 */

import { CLAUSES } from '../shared/editorial.js';
/*
 * **EVERY INTERPOLATION ON THIS PAGE, and `escape.ts`'s own docblock says why
 * it took a review to notice.** It reads "the three live surfaces are built
 * from `document.createElement` and `textContent`, which escapes by
 * construction", and names the profile page as "the first thing here that
 * builds HTML as a string". This editor was the SECOND and arrived without the
 * helper: a title, a clause code and a clause body all went into `innerHTML`
 * raw, one of them inside an attribute value.
 *
 * **It was self-inflicted and it was one change away from not being.** The only
 * data reaching these templates is the signed-in person's own, because the list
 * is filtered on their handle and the editor loads from it, so the worst
 * available outcome was a person injecting into their own page. The moment a
 * PUBLIC directory renders another owner's title, which
 * `docs/plans/2026-09-05-the-site-describes-a-different-product.md` §17.5 asks
 * for, the same templates become stored. Escaping at the boundary now is what
 * stops that being a security change later, in a commit about listing
 * channels.
 *
 * The floor is escaped too, though it is `CLAUSES` and therefore code-owned.
 * "Escape everything written into markup" is a rule the next edit cannot get
 * wrong; "escape the half that came from a person" is one it can.
 */
import { escapeHtml } from '../shared/escape.js';
import { CHANNEL_ID, MIN_SHOT_BOUNDS, reservedChannelRefusal } from '../shared/channel.js';
import type { Clause } from '../shared/editorial.js';
import { account, token } from './session.js';
import { mountNav } from './nav.js';

const $ = <T extends HTMLElement>(id: string): T => document.getElementById(id) as T;

const signedOut = $<HTMLDivElement>('signedout');
const mine = $<HTMLElement>('mine');
const mineList = $<HTMLUListElement>('minelist');
const editor = $<HTMLElement>('editor');
const editorTitle = $<HTMLHeadingElement>('editortitle');
const cid = $<HTMLInputElement>('cid');
const cidHint = $<HTMLParagraphElement>('cidhint');
const ctitle = $<HTMLInputElement>('ctitle');
const cshot = $<HTMLInputElement>('cshot');
const clausesEl = $<HTMLDivElement>('clauses');
const versionNote = $<HTMLParagraphElement>('versionnote');
const saveMsg = $<HTMLParagraphElement>('savemsg');

interface OwnedChannel {
  id: string; title: string; clauses: Clause[]; minShotMs: number;
  version: string; ownerId: string | null; ownerHandle: string | null;
}

let editing: OwnedChannel | null = null;
let draftClauses: Clause[] = [];
let myHandle: string | null = null;

/** The floor, as the reviewer receives it. */
function renderFloor(): void {
  const byGate = new Map<string, Clause[]>();
  for (const c of CLAUSES) {
    const list = byGate.get(c.gate) ?? [];
    list.push(c);
    byGate.set(c.gate, list);
  }
  const parts: string[] = [];
  for (const [gate, list] of byGate) {
    parts.push(`<h4>${escapeHtml(gate)}</h4><ul class="floor">`);
    for (const c of list) {
      /*
       * `kind` is shown because it is the distinction an owner most needs and
       * is least likely to guess: a `permitted` clause reads to the reviewer as
       * "NOT grounds to reject", which is why a channel may not add one.
       */
      parts.push(
        `<li><code>${escapeHtml(c.code)}</code> `
        + `<span class="kind kind-${escapeHtml(c.kind)}">${escapeHtml(c.kind)}</span>`
        + `<br>${escapeHtml(c.text)}</li>`,
      );
    }
    parts.push('</ul>');
  }
  $<HTMLDivElement>('floor').innerHTML = parts.join('');
}

function renderClauses(): void {
  if (draftClauses.length === 0) {
    clausesEl.innerHTML = '<p class="hint">No rules of your own yet. A channel with none '
      + 'applies exactly the standard below, which is a real choice rather than an empty one.</p>';
    return;
  }
  clausesEl.innerHTML = draftClauses.map((c, i) => `
    <div class="clause-row" data-i="${String(i)}">
      <input class="c-code" value="${escapeHtml(c.code)}" placeholder="${escapeHtml(cid.value || 'channel')}.no-betting">
      <select class="c-gate">
        <option value="visual"${escapeHtml(c.gate === 'visual' ? ' selected' : '')}>picture</option>
        <option value="speech"${escapeHtml(c.gate === 'speech' ? ' selected' : '')}>speech</option>
      </select>
      <textarea class="c-text" rows="2" placeholder="Reject anything that ...">${escapeHtml(c.text)}</textarea>
      <button type="button" class="c-del secondary" data-i="${String(i)}">Remove</button>
      <p class="clause-error" data-err="${escapeHtml(c.code)}"></p>
    </div>`).join('');
}

function readClauses(): Clause[] {
  return [...clausesEl.querySelectorAll<HTMLDivElement>('.clause-row')].map((row) => ({
    code: (row.querySelector<HTMLInputElement>('.c-code')?.value ?? '').trim(),
    gate: (row.querySelector<HTMLSelectElement>('.c-gate')?.value ?? 'visual') as Clause['gate'],
    /*
     * `reject` is not offered as a choice. A channel may only be STRICTER, so a
     * `permitted` clause is refused on publish anyway, and offering it in a
     * dropdown would invite an owner to write one and be told no. The one place
     * this is explained is the floor panel, where `permitted` clauses are
     * visible and labelled.
     */
    kind: 'reject' as const,
    text: (row.querySelector<HTMLTextAreaElement>('.c-text')?.value ?? '').trim(),
    disclosure: 'public' as const,
  }));
}

/**
 * What publishing will do, said BEFORE the save.
 *
 * §16.4's receipt only means something if the owner knows they are creating one.
 */
function renderVersionNote(): void {
  versionNote.textContent = editing === null
    ? 'Publishing creates version 1. Every later edit publishes a new version, and the old '
      + 'one stays readable, so a ruling made under it can still be looked up.'
    : `Publishing will create the next version of #${editing.id}. The current one stays `
      + 'readable and any ruling made under it still cites it. Nothing is overwritten.';
}

function showEditor(channel: OwnedChannel | null): void {
  editing = channel;
  draftClauses = channel ? [...channel.clauses] : [];
  editorTitle.textContent = channel ? `Edit #${channel.id}` : 'Make a channel';
  cid.value = channel?.id ?? '';
  cid.disabled = channel !== null;
  cidHint.textContent = channel !== null
    ? 'A channel keeps its name. Rulings cite it, so renaming would break every one of them.'
    : 'Lower case letters, digits and hyphens. This is what people type after the hash.';
  ctitle.value = channel?.title ?? '';
  cshot.value = String(Math.round((channel?.minShotMs ?? 90_000) / 1000));
  saveMsg.textContent = '';
  renderClauses();
  renderVersionNote();
  editor.hidden = false;
}

async function loadMine(): Promise<void> {
  const res = await fetch('/api/channels');
  if (!res.ok) {
    mineList.innerHTML = '<li>The channel store did not answer. This is not a list of no '
      + 'channels: the rules already published are still being applied.</li>';
    return;
  }
  const body = (await res.json()) as { channels: OwnedChannel[] };
  const owned = body.channels.filter((c) => c.ownerHandle !== null && c.ownerHandle === myHandle);
  mineList.innerHTML = owned.length === 0
    ? '<li class="hint">None yet.</li>'
    : owned.map((c) => `<li><strong>#${escapeHtml(c.id)}</strong> ${escapeHtml(c.title)}
        <button type="button" class="edit secondary" data-id="${escapeHtml(c.id)}">Edit</button>
        <a href="/api/channels/${encodeURIComponent(c.id)}/versions">history</a></li>`).join('');
  for (const b of mineList.querySelectorAll<HTMLButtonElement>('.edit')) {
    b.addEventListener('click', () => {
      const found = owned.find((c) => c.id === b.dataset.id);
      if (found) showEditor(found);
    });
  }
}

/**
 * A refusal is attached to the clause that caused it, not shown as a banner.
 *
 * The floor's three refusals each name a clause code, so the page can put the
 * sentence where the person is looking. A banner makes the reader hunt.
 */
function showRefusal(reason: string): void {
  saveMsg.textContent = reason;
  saveMsg.className = 'refusal';
  for (const el of clausesEl.querySelectorAll<HTMLParagraphElement>('.clause-error')) {
    el.textContent = '';
  }
  for (const row of clausesEl.querySelectorAll<HTMLDivElement>('.clause-row')) {
    const code = row.querySelector<HTMLInputElement>('.c-code')?.value.trim() ?? '';
    if (code !== '' && reason.includes(code)) {
      const err = row.querySelector<HTMLParagraphElement>('.clause-error');
      if (err) err.textContent = reason;
    }
  }
}

async function save(): Promise<void> {
  const t = await token();
  if (t === null) {
    saveMsg.textContent = 'your session expired. Sign in again.';
    return;
  }
  const seconds = Number(cshot.value);
  const minShotMs = Math.round(seconds * 1000);
  if (!Number.isFinite(minShotMs)
      || minShotMs < MIN_SHOT_BOUNDS.min || minShotMs > MIN_SHOT_BOUNDS.max) {
    saveMsg.textContent = `a minimum shot is between ${String(MIN_SHOT_BOUNDS.min / 1000)} and `
      + `${String(MIN_SHOT_BOUNDS.max / 1000)} seconds.`;
    return;
  }
  const payload = {
    id: cid.value.trim(),
    title: ctitle.value.trim(),
    minShotMs,
    clauses: readClauses(),
  };
  if (editing === null && !CHANNEL_ID.test(payload.id)) {
    saveMsg.textContent = 'a channel name is lower case letters, digits and hyphens, and starts '
      + 'and ends with a letter or digit.';
    return;
  }
  /*
   * **THE SAME PREDICATE THE SERVER REFUSES ON, not a copy of the list.**
   *
   * `D1` to `D3`'s rule is that a control advertises what the gate would do,
   * and this form let somebody type `all`, write a title, add clauses and press
   * Publish before learning the name was never available. The server's refusal
   * was good and arrived after the work.
   *
   * `reservedChannelRefusal` is pure and shared, so this is the same call the
   * route makes rather than a second answer that can drift, and the message a
   * person reads here is the one the server would have sent. The server check
   * is unchanged and load-bearing: this is a courtesy, and a browser is not a
   * gate.
   */
  const reserved = editing === null ? reservedChannelRefusal(payload.id) : null;
  if (reserved !== null) {
    saveMsg.textContent = reserved;
    return;
  }
  saveMsg.className = '';
  saveMsg.textContent = 'publishing...';
  const res = await fetch(
    editing === null ? '/api/channels' : `/api/channels/${editing.id}`,
    {
      method: editing === null ? 'POST' : 'PATCH',
      headers: { Authorization: `Bearer ${t}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    },
  );
  const body = (await res.json()) as { version?: number; error?: string };
  if (!res.ok) {
    showRefusal(body.error ?? 'that was refused and the reason did not arrive, which is a fault here.');
    return;
  }
  saveMsg.className = '';
  saveMsg.textContent = `published version ${String(body.version ?? '?')}.`;
  editor.hidden = true;
  await loadMine();
}

async function main(): Promise<void> {
  mountNav();
  renderFloor();
  const who = await account();
  if (who === null) {
    signedOut.hidden = false;
    return;
  }
  myHandle = who.handle;
  mine.hidden = false;
  await loadMine();
}

$<HTMLButtonElement>('new').addEventListener('click', () => { showEditor(null); });
$<HTMLButtonElement>('cancel').addEventListener('click', () => { editor.hidden = true; });
$<HTMLButtonElement>('save').addEventListener('click', () => { void save(); });
$<HTMLButtonElement>('addclause').addEventListener('click', () => {
  draftClauses = [...readClauses(), {
    code: `${cid.value.trim() || 'channel'}.`, gate: 'visual', kind: 'reject',
    text: '', disclosure: 'public',
  }];
  renderClauses();
});
clausesEl.addEventListener('click', (e) => {
  const t = e.target as HTMLElement;
  if (!t.classList.contains('c-del')) return;
  const i = Number(t.dataset.i);
  draftClauses = readClauses().filter((_, n) => n !== i);
  renderClauses();
});

void main();
